import { useEffect, useRef, useState } from "react";
import "@/App.css";
import Marquee from "react-fast-marquee";
import {
  Briefcase,
  Settings,
  BarChart3,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Download,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  Award,
} from "lucide-react";

const NAV_LINKS = [
  { id: "what-we-do", label: "What We Do" },
  { id: "why-us", label: "Why Us" },
  { id: "results", label: "Results" },
  { id: "capabilities", label: "Capabilities" },
  { id: "leadership", label: "Leadership" },
  { id: "contact", label: "Contact" },
];

const HERO_BG =
  "https://images.pexels.com/photos/32655956/pexels-photo-32655956.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";
const HEADSHOT =
  "https://customer-assets.emergentagent.com/job_pollard-unified/artifacts/02xpjydd_Screenshot_20260420_205448_Chrome%281%29%5B1%5D.jpg";
const CAPABILITY_PDF = "/assets/Pollard-Capital-Capability-Statement.pdf";
const CONTACT_EMAIL = "info@pollardcapital.com";
const CONTACT_PHONE = "(404) 585-0130";

const MAILTO = `mailto:${CONTACT_EMAIL}?subject=Pollard%20Capital%20Inquiry`;

/* ------------------- Hooks ------------------- */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const useCountUp = (target, active, duration = 1600) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return value;
};

/* ------------------- Sub-components ------------------- */
const NavBrand = ({ scrolled, onClick }) => (
  <button
    data-testid="nav-logo"
    onClick={onClick}
    className="flex items-center gap-3 group"
  >
    <div className="w-9 h-9 bg-[#0A1128] text-white flex items-center justify-center font-display font-bold text-lg">
      P
    </div>
    <div className="leading-tight text-left">
      <div
        className={`font-display font-bold text-[0.95rem] tracking-tight ${
          scrolled ? "text-[#0A1128]" : "text-white"
        }`}
      >
        POLLARD CAPITAL
      </div>
      <div
        className={`label-mono text-[0.62rem] ${
          scrolled ? "text-[#4B5563]" : "text-white/70"
        }`}
      >
        LLC · EST. 2016
      </div>
    </div>
  </button>
);

const DesktopNav = ({ scrolled, onNavigate }) => (
  <nav className="hidden md:flex items-center gap-8">
    {NAV_LINKS.map((l) => (
      <button
        key={l.id}
        data-testid={`nav-link-${l.id}`}
        onClick={() => onNavigate(l.id)}
        className={`nav-link ${scrolled ? "" : "!text-white/85 hover:!text-white"}`}
      >
        {l.label}
      </button>
    ))}
    <a
      data-testid="nav-cta-contact"
      href={MAILTO}
      className="btn-primary px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
    >
      Get in Touch <ArrowRight size={16} />
    </a>
  </nav>
);

const MobileMenu = ({ onNavigate }) => (
  <div
    data-testid="nav-mobile-menu"
    className="md:hidden bg-white border-t border-[#E5E7EB]"
  >
    <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1">
      {NAV_LINKS.map((l) => (
        <button
          key={l.id}
          data-testid={`nav-mobile-link-${l.id}`}
          onClick={() => onNavigate(l.id)}
          className="text-left py-3 border-b border-[#E5E7EB] font-display text-[#0A1128]"
        >
          {l.label}
        </button>
      ))}
      <a
        data-testid="nav-mobile-cta"
        href={MAILTO}
        className="btn-primary mt-3 px-5 py-3 text-center font-medium"
      >
        Get in Touch
      </a>
    </div>
  </div>
);

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavigate = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-[#E5E7EB]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between h-16 md:h-20">
        <NavBrand scrolled={scrolled} onClick={scrollToTop} />
        <DesktopNav scrolled={scrolled} onNavigate={handleNavigate} />
        <button
          data-testid="nav-mobile-toggle"
          className={`md:hidden ${scrolled ? "text-[#0A1128]" : "text-white"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && <MobileMenu onNavigate={handleNavigate} />}
    </header>
  );
};

/* ------------------- Presentational sub-components ------------------- */
const HeroBackdrop = () => (
  <>
    <div
      aria-hidden
      className="absolute inset-0 bg-center bg-cover opacity-30"
      style={{ backgroundImage: `url(${HERO_BG})` }}
    />
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(10,17,40,0.85) 0%, rgba(10,17,40,0.92) 60%, rgba(10,17,40,1) 100%)",
      }}
    />
    <div aria-hidden className="absolute inset-0 grid-bg opacity-60" />
  </>
);

const HeroCertStrip = () => (
  <>
    <div className="mt-16 pt-8 border-t border-white/10 reveal">
      <div className="flex flex-wrap items-center gap-x-8 gap-y-3 label-mono text-white/60">
        <span>PMP Certified</span>
        <span className="text-white/20">/</span>
        <span>AABE</span>
        <span className="text-white/20">·</span>
        <span>GDOT DBE</span>
        <span className="text-white/20">·</span>
        <span>SBE</span>
        <span className="text-white/20">·</span>
        <span>Six Sigma Black Belt</span>
      </div>
    </div>
    <div
      data-testid="hero-gov-signal"
      className="mt-4 label-mono text-[#00A859] flex items-center gap-2 reveal"
    >
      <span className="inline-block w-5 h-px bg-[#00A859]" />
      Supporting public and private sector clients
    </div>
  </>
);

const HeroCTAs = () => (
  <div className="mt-12 flex flex-wrap gap-4 reveal">
    <a
      data-testid="hero-cta-contact"
      href={MAILTO}
      className="btn-primary px-7 py-4 text-base font-medium inline-flex items-center gap-2"
    >
      Contact Pollard Capital <ArrowRight size={18} />
    </a>
    <a
      data-testid="hero-cta-download"
      href={CAPABILITY_PDF}
      download
      className="btn-capability group px-7 py-4 text-base font-semibold inline-flex items-center gap-3"
    >
      <span className="btn-capability-icon">
        <Download size={18} />
      </span>
      <span>Download Capability Statement</span>
      <ArrowRight size={18} className="btn-capability-arrow" />
    </a>
  </div>
);

const Hero = () => (
  <section
    id="home"
    data-testid="hero-section"
    className="relative overflow-hidden grain"
    style={{ backgroundColor: "#0A1128" }}
  >
    <HeroBackdrop />

    <div className="relative max-w-7xl mx-auto px-6 md:px-8 pt-36 md:pt-44 pb-24 md:pb-32 min-h-[92vh] flex flex-col justify-center">
      <div
        data-testid="hero-kicker"
        className="flex items-center gap-3 text-white/80 label-mono mb-8 reveal"
      >
        <span className="live-dot" />
        <span>Pollard Capital · Consulting · Marietta, GA</span>
      </div>

      <h1
        data-testid="hero-headline"
        className="font-display font-extrabold text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl reveal"
      >
        Program Management.
        <br />
        Business Operations.
        <br />
        <span className="text-[#00A859]">Data-Driven Results.</span>
      </h1>

      <p
        data-testid="hero-sub"
        className="mt-8 text-white/75 text-lg md:text-xl max-w-2xl leading-relaxed reveal"
      >
        We deliver structured program execution and measurable operational
        outcomes — combining technical expertise, financial acumen, and
        disciplined delivery.
      </p>

      <p
        data-testid="hero-credibility"
        className="mt-6 font-display text-[#00A859] text-lg md:text-xl max-w-3xl leading-snug reveal"
      >
        Delivering measurable outcomes across complex programs, operations,
        and data environments.
      </p>

      <HeroCTAs />
      <HeroCertStrip />

      <div className="mt-14 label-mono text-white/40 flex flex-col items-start">
        <span>Scroll</span>
        <div className="scroll-tick mx-0" />
      </div>
    </div>
  </section>
);

const SectionHeading = ({ overline, title, desc, testid }) => (
  <div className="mb-14 md:mb-16 reveal max-w-3xl">
    <div
      data-testid={`${testid}-overline`}
      className="label-mono text-[#00A859] mb-5 flex items-center gap-2"
    >
      <span className="inline-block w-8 h-px bg-[#00A859]" />
      {overline}
    </div>
    <h2
      data-testid={`${testid}-title`}
      className="font-display font-bold text-[#0A1128] text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
    >
      {title}
    </h2>
    {desc && (
      <p className="mt-6 text-[#4B5563] text-lg max-w-2xl leading-relaxed">
        {desc}
      </p>
    )}
  </div>
);

const SERVICES = [
  {
    icon: Briefcase,
    number: "01",
    title: "Program Management",
    sub: "End-to-end execution",
    desc:
      "PMP-certified delivery of complex, multi-stakeholder programs from charter through close-out — scope, schedule, cost, and risk under one integrated plan.",
  },
  {
    icon: Settings,
    number: "02",
    title: "Business Operations",
    sub: "Process optimization",
    desc:
      "Six Sigma-driven process redesign and operational scaling — cutting cycle time, reducing defects, and embedding measurable controls into daily execution.",
  },
  {
    icon: BarChart3,
    number: "03",
    title: "Data & Analytics",
    sub: "SQL, Python & BI",
    desc:
      "Custom SQL reporting, Python analytics, and BI frameworks that turn raw operational data into cost, schedule, and performance visibility leaders act on.",
  },
  {
    icon: ShieldCheck,
    number: "04",
    title: "Compliance & Governance",
    sub: "Audit & controls",
    desc:
      "Audit-ready documentation, EVM cost controls, and compliance frameworks for regulated government and private-sector programs.",
  },
];

const WhatWeDo = () => (
  <section
    id="what-we-do"
    data-testid="what-we-do-section"
    className="relative py-24 md:py-32 bg-white"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <SectionHeading
        overline="01 · What We Do"
        title="Four disciplines. One integrated delivery engine."
        desc="Pollard Capital brings program leadership, operational rigor, data analytics, and governance together so initiatives don't just launch — they perform."
        testid="what-we-do"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-[#E5E7EB]">
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <div
              key={s.title}
              data-testid={`service-tile-${i}`}
              className="tile reveal border-r border-b border-[#E5E7EB] !border-l-0 !border-t-0"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <span className="tile-accent" />
              <div className="flex items-start justify-between mb-8">
                <div className="w-12 h-12 bg-[#0A1128] text-white flex items-center justify-center">
                  <Icon size={22} strokeWidth={1.6} />
                </div>
                <span className="label-mono text-[#9CA3AF]">{s.number}</span>
              </div>
              <div className="label-mono text-[#00A859] mb-2">{s.sub}</div>
              <h3 className="font-display font-bold text-[#0A1128] text-2xl md:text-3xl mb-4 tracking-tight">
                {s.title}
              </h3>
              <p className="text-[#4B5563] leading-relaxed">{s.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const WHY = [
  {
    t: "Proven Cost Savings",
    d: "$2M+ delivered through process improvement and EVM discipline.",
  },
  {
    t: "Government & Industry Experience",
    d: "Regulated programs and commercial operations across real estate, logistics, and construction.",
  },
  {
    t: "Hands-On Leadership",
    d: "Founder-led engagements — no hand-offs, no diluted accountability.",
  },
  {
    t: "Data-Driven Execution",
    d: "SQL and BI frameworks embedded from day one, not bolted on at close-out.",
  },
];

const PMP_CHIPS = [
  { k: "MBA", v: "Business" },
  { k: "6σ BB", v: "Six Sigma" },
  { k: "OSHA 10", v: "Safety" },
];

const PMPBadge = () => (
  <div className="relative">
    <div
      aria-hidden
      className="absolute -top-4 -left-4 right-10 bottom-10 border border-[#0A1128]"
    />
    <div
      data-testid="why-us-badge"
      className="relative aspect-[4/5] bg-[#0A1128] overflow-hidden flex flex-col"
    >
      <div aria-hidden className="absolute inset-0 grid-bg opacity-50" />

      <div className="relative flex items-center justify-between px-6 pt-6">
        <div className="label-mono text-[#00A859] flex items-center gap-2">
          <span className="inline-block w-8 h-px bg-[#00A859]" />
          Certified
        </div>
        <div className="label-mono text-white/50">EST · 2016</div>
      </div>

      <div className="relative flex-1 flex flex-col items-center justify-center px-6">
        <div className="relative" style={{ width: "220px", height: "240px" }}>
          <svg
            viewBox="0 0 100 110"
            className="absolute inset-0 w-full h-full"
            aria-hidden="true"
          >
            <polygon
              points="50,3 94,28 94,82 50,107 6,82 6,28"
              fill="none"
              stroke="#00A859"
              strokeWidth="2"
            />
            <polygon
              points="50,10 88,31 88,79 50,100 12,79 12,31"
              fill="none"
              stroke="rgba(255,255,255,0.12)"
              strokeWidth="0.5"
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div
              className="font-display font-extrabold text-white leading-none tracking-tight"
              style={{ fontSize: "78px" }}
            >
              PMP
            </div>
            <div className="mt-2 w-10 h-px bg-[#00A859]" />
            <div className="mt-2 label-mono text-[#00A859] text-[0.55rem]">
              PMI CERTIFIED
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <div className="font-display font-bold text-white text-sm tracking-wider uppercase">
            Project Management
          </div>
          <div className="font-display font-bold text-white/70 text-sm tracking-wider uppercase">
            Professional
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/10 grid grid-cols-3">
        {PMP_CHIPS.map((c, i) => (
          <div
            key={c.k}
            className={`px-4 py-4 text-left ${i < 2 ? "border-r border-white/10" : ""}`}
          >
            <div className="font-display font-bold text-white text-sm">
              {c.k}
            </div>
            <div className="label-mono text-white/50 text-[0.58rem] mt-1">
              {c.v}
            </div>
          </div>
        ))}
      </div>
    </div>
    <div className="absolute -bottom-5 -right-5 bg-[#00A859] text-white px-5 py-3 label-mono">
      AABE · DBE · SBE
    </div>
  </div>
);

const WhyRow = ({ index, title, description }) => (
  <div
    data-testid={`why-row-${index}`}
    className="grid grid-cols-12 gap-4 items-start py-6 border-b border-[#E5E7EB] group"
  >
    <div className="col-span-2 md:col-span-1 label-mono text-[#9CA3AF] pt-1">
      0{index + 1}
    </div>
    <div className="col-span-10 md:col-span-4">
      <div className="flex items-start gap-3">
        <CheckCircle2
          size={20}
          className="text-[#00A859] shrink-0 mt-0.5"
          strokeWidth={2}
        />
        <h3 className="font-display font-bold text-[#0A1128] text-lg md:text-xl tracking-tight">
          {title}
        </h3>
      </div>
    </div>
    <div className="col-span-12 md:col-span-7 text-[#4B5563] leading-relaxed pl-9 md:pl-0">
      {description}
    </div>
  </div>
);

const WhyUs = () => (
  <section
    id="why-us"
    data-testid="why-us-section"
    className="relative py-24 md:py-32 bg-[#F1F3F5]"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-5 reveal">
          <PMPBadge />
        </div>

        <div className="lg:col-span-7 reveal" style={{ transitionDelay: "120ms" }}>
          <div className="label-mono text-[#00A859] mb-5 flex items-center gap-2">
            <span className="inline-block w-8 h-px bg-[#00A859]" />
            02 · Why Pollard Capital
          </div>
          <h2 className="font-display font-bold text-[#0A1128] text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] mb-10">
            A consultancy built for organizations that expect outcomes, not decks.
          </h2>

          <div className="border-t border-[#E5E7EB]">
            {WHY.map((w, i) => (
              <WhyRow key={w.t} index={i} title={w.t} description={w.d} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const STATS = [
  { v: 2, prefix: "$", suffix: "M+", label: "Cost Savings Delivered" },
  { v: 75, prefix: "$", suffix: "M", label: "Program Budgets Managed" },
  { v: 30, prefix: "", suffix: "%", label: "Performance Improvement" },
  { v: 10, prefix: "", suffix: "+", label: "Years of Expertise" },
];

const StatCard = ({ s, active, delay }) => {
  const value = useCountUp(s.v, active);
  const display =
    s.v >= 10 ? Math.floor(value).toString() : value.toFixed(0);
  return (
    <div
      data-testid={`stat-card-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
      className="stat-xl reveal"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="label-mono text-[#00A859] mb-8 flex items-center gap-2">
        <span className="inline-block w-6 h-px bg-[#00A859]" />
        Metric · 0{Math.min(4, Math.max(1, delay / 80 + 1))}
      </div>
      <div className="font-display font-extrabold text-white text-7xl sm:text-8xl md:text-8xl lg:text-[9rem] tracking-tight leading-[0.9] mb-8">
        {s.prefix}
        <span>{display}</span>
        <span className="text-[#00A859]">{s.suffix}</span>
      </div>
      <div className="text-white/75 text-base md:text-lg leading-relaxed max-w-[22ch] font-medium">
        {s.label}
      </div>
    </div>
  );
};

const Results = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setActive(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="results"
      ref={ref}
      data-testid="results-section"
      className="relative py-28 md:py-40 grain overflow-hidden"
      style={{ backgroundColor: "#050814" }}
    >
      {/* Top + bottom green signal bars */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px] bg-[#00A859]" />
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(0,168,89,0.6) 50%, transparent)",
        }}
      />
      {/* Heavy grid */}
      <div aria-hidden className="absolute inset-0 grid-bg opacity-70" />
      {/* Radial glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px circle at 80% 10%, rgba(0,168,89,0.15), transparent 60%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-8">
        <div className="mb-20 md:mb-24 reveal max-w-4xl">
          <div className="label-mono text-[#00A859] mb-6 flex items-center gap-2">
            <span className="inline-block w-8 h-px bg-[#00A859]" />
            03 · Proven Results
          </div>
          <h2 className="font-display font-bold text-white text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]">
            Numbers we've already put on the board.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-[#1a2744]">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className="border-r border-b border-[#1a2744]"
            >
              <StatCard s={s} active={active} delay={i * 80} />
            </div>
          ))}
        </div>

        <div
          className="mt-12 text-white/50 text-sm label-mono reveal"
          style={{ transitionDelay: "360ms" }}
        >
          · Sources: client programs 2016 – present · Figures from past performance
        </div>
      </div>
    </section>
  );
};

const CAPS = [
  "Program & Project Management (PMP)",
  "Business Operations & Process Optimization",
  "Data Analytics & BI — SQL, Python",
  "Financial Analysis & Capital Allocation",
  "Earned Value Management (EVM)",
  "Cost Control & Budget Discipline",
  "Compliance, Governance & Audit Readiness",
  "Logistics & Infrastructure Program Support",
  "Change Management & Organizational Scaling",
  "BI Frameworks — Real Estate, Logistics, Construction",
];

const Capabilities = () => (
  <section
    id="capabilities"
    data-testid="capabilities-section"
    className="relative py-24 md:py-32 bg-white overflow-hidden"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <SectionHeading
        overline="04 · Core Capabilities"
        title="A disciplined toolkit across the full program lifecycle."
        testid="capabilities"
      />
    </div>

    <div className="reveal">
      <Marquee gradient={false} speed={38} pauseOnHover>
        {CAPS.map((c) => (
          <div key={c} className="marquee-item" data-testid="capability-chip">
            <span className="w-1.5 h-1.5 bg-[#00A859]" />
            {c}
          </div>
        ))}
      </Marquee>
    </div>

    <div className="max-w-7xl mx-auto px-6 md:px-8 mt-16">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-l border-[#E5E7EB]">
        {[
          { k: "NAICS", v: "541611 · 541613 · 541614 · 541618" },
          { k: "DUNS", v: "080489828" },
          { k: "CAGE", v: "7RWY7" },
          { k: "Founded", v: "Sept 1, 2016" },
        ].map((m) => (
          <div
            key={m.k}
            data-testid={`meta-${m.k.toLowerCase()}`}
            className="border-r border-b border-[#E5E7EB] p-6 reveal"
          >
            <div className="label-mono text-[#9CA3AF] mb-2">{m.k}</div>
            <div className="font-mono font-medium text-[#0A1128] text-sm md:text-base break-words">
              {m.v}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CapabilityPdfCard = () => (
  <div className="relative bg-white p-8 md:p-10 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.55)]">
    <div aria-hidden className="absolute -top-1 left-0 right-0 h-1 bg-[#00A859]" />
    <div className="flex items-start justify-between mb-8">
      <div>
        <div className="label-mono text-[#9CA3AF] mb-2">PDF · 1 page</div>
        <div className="font-display font-bold text-[#0A1128] text-xl leading-tight">
          Pollard Capital
          <br />
          Capability Statement
        </div>
      </div>
      <div className="w-12 h-12 bg-[#0A1128] text-[#00A859] flex items-center justify-center">
        <Award size={22} strokeWidth={1.6} />
      </div>
    </div>
    <a
      data-testid="download-cta"
      href={CAPABILITY_PDF}
      download
      className="btn-primary w-full px-6 py-5 text-base font-semibold inline-flex items-center justify-center gap-3"
    >
      <Download size={18} /> Download Capability Statement
      <ArrowRight size={18} />
    </a>
    <div className="mt-5 text-center label-mono text-[#9CA3AF]">
      336 KB · Updated 2026
    </div>
  </div>
);

const DownloadBlock = () => (
  <section
    data-testid="download-section"
    className="relative py-28 md:py-36 grain overflow-hidden"
    style={{ backgroundColor: "#0A1128" }}
  >
    {/* Green signal bars */}
    <div aria-hidden className="absolute top-0 left-0 right-0 h-[3px] bg-[#00A859]" />
    <div
      aria-hidden
      className="absolute bottom-0 left-0 right-0 h-px"
      style={{
        background:
          "linear-gradient(to right, transparent, rgba(0,168,89,0.5) 50%, transparent)",
      }}
    />
    <div aria-hidden className="absolute inset-0 grid-bg opacity-50" />
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(700px circle at 15% 110%, rgba(0,168,89,0.18), transparent 60%)",
      }}
    />

    <div className="relative max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 reveal">
        <div className="label-mono text-[#00A859] mb-6 flex items-center gap-2">
          <span className="inline-block w-8 h-px bg-[#00A859]" />
          05 · Capability Statement
        </div>
        <h2 className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
          The full record — expertise, certifications, and past performance.
        </h2>
        <p className="mt-6 text-white/70 text-lg max-w-2xl leading-relaxed">
          One PDF. Everything a procurement officer, prime, or partner needs to
          evaluate Pollard Capital.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 label-mono text-white/50">
          <span>NAICS 541611</span>
          <span className="text-white/20">·</span>
          <span>AABE · DBE · SBE</span>
          <span className="text-white/20">·</span>
          <span>PMP Certified</span>
        </div>
      </div>
      <div className="lg:col-span-5 reveal" style={{ transitionDelay: "140ms" }}>
        <CapabilityPdfCard />
      </div>
    </div>
  </section>
);

const Leadership = () => (
  <section
    id="leadership"
    data-testid="leadership-section"
    className="relative py-24 md:py-32 bg-white"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-8">
      <SectionHeading
        overline="06 · Leadership"
        title="Founder-led, outcome-owned."
        testid="leadership"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-4 reveal">
          <div className="aspect-[4/5] overflow-hidden bg-[#0A1128] border border-[#E5E7EB]">
            <img
              data-testid="leadership-image"
              src={HEADSHOT}
              alt="Emmett G. Pollard Jr"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </div>
        </div>
        <div className="lg:col-span-8 reveal" style={{ transitionDelay: "140ms" }}>
          <div className="label-mono text-[#9CA3AF] mb-3">Founder & Program Manager</div>
          <h3 className="font-display font-bold text-[#0A1128] text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Emmett G. Pollard, Jr
          </h3>
          <div className="mt-2 font-mono text-[#00A859] text-sm tracking-widest">
            MBA · PMP · SIX SIGMA BLACK BELT · OSHA 10
          </div>

          <p className="mt-8 text-[#4B5563] text-lg leading-relaxed max-w-2xl">
            Program management professional with 10+ years leading
            cross-functional initiatives across logistics, infrastructure,
            and regulated environments.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-0 border-l border-t border-[#E5E7EB]">
            {[
              { k: "Experience", v: "10+ Years" },
              { k: "Sectors", v: "Gov · Real Estate · Logistics" },
              { k: "Tools", v: "SQL · Python · BI" },
              { k: "Savings", v: "$2M+ Delivered" },
              { k: "Budgets", v: "Up to $75M" },
              { k: "Base", v: "Marietta, GA" },
            ].map((m) => (
              <div
                key={m.k}
                className="border-r border-b border-[#E5E7EB] p-5"
              >
                <div className="label-mono text-[#9CA3AF] mb-2">{m.k}</div>
                <div className="font-display font-bold text-[#0A1128] text-base">
                  {m.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const CONTACT_CARDS = [
  { Icon: Mail, label: "Email", value: CONTACT_EMAIL, href: MAILTO },
  {
    Icon: Phone,
    label: "Phone",
    value: CONTACT_PHONE,
    href: `tel:+1${CONTACT_PHONE.replace(/\D/g, "")}`,
  },
  {
    Icon: MapPin,
    label: "Mail",
    value: "P.O. Box 674714, Marietta, GA 30006",
    href: null,
  },
];

const ContactCard = ({ Icon, label, value, href }) => {
  const className =
    "border-r border-b border-[#1E293B] p-8" +
    (href ? " hover:bg-white/[0.02] transition-colors" : "");
  const body = (
    <>
      <Icon className="text-[#00A859] mb-5" size={22} strokeWidth={1.6} />
      <div className="label-mono text-white/50 mb-2">{label}</div>
      <div className="font-display text-white text-base md:text-lg break-words">
        {value}
      </div>
    </>
  );
  const testId = `contact-card-${label.toLowerCase()}`;
  return href ? (
    <a href={href} data-testid={testId} className={className}>
      {body}
    </a>
  ) : (
    <div data-testid={testId} className={className}>
      {body}
    </div>
  );
};

const ContactCTAs = () => (
  <div className="mt-12 flex flex-wrap gap-4 reveal">
    <a
      data-testid="contact-cta-email"
      href={MAILTO}
      className="btn-primary px-8 py-5 font-medium inline-flex items-center gap-2"
    >
      <Mail size={18} /> {CONTACT_EMAIL}
    </a>
    <a
      data-testid="contact-cta-download"
      href={CAPABILITY_PDF}
      download
      className="btn-capability px-8 py-5 font-semibold inline-flex items-center gap-3"
    >
      <span className="btn-capability-icon">
        <Download size={18} />
      </span>
      Download Capability Statement
      <ArrowRight size={18} className="btn-capability-arrow" />
    </a>
  </div>
);

const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="relative py-24 md:py-36 grain overflow-hidden"
    style={{ backgroundColor: "#0A1128" }}
  >
    <div aria-hidden className="absolute inset-0 grid-bg opacity-40" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-8">
      <div className="label-mono text-[#00A859] mb-6 flex items-center gap-2 reveal">
        <span className="inline-block w-8 h-px bg-[#00A859]" />
        07 · Let's Work Together
      </div>
      <h2 className="font-display font-extrabold text-white text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] reveal">
        Ready to put <span className="text-[#00A859]">disciplined</span>
        <br />
        execution on your program?
      </h2>

      <ContactCTAs />

      <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[#1E293B] reveal">
        {CONTACT_CARDS.map((c) => (
          <ContactCard
            key={c.label}
            Icon={c.Icon}
            label={c.label}
            value={c.value}
            href={c.href}
          />
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="bg-[#0A1128] border-t border-[#1E293B] py-10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-[#00A859] text-white flex items-center justify-center font-display font-bold">
          P
        </div>
        <div className="leading-tight">
          <div className="font-display font-bold text-white text-sm">
            POLLARD CAPITAL, LLC
          </div>
          <div className="label-mono text-white/50 text-[0.62rem]">
            Marietta, GA · Est. 2016
          </div>
        </div>
      </div>
      <div className="label-mono text-white/40 text-[0.7rem]">
        © 2026 Pollard Capital, LLC · All Rights Reserved
      </div>
      <div className="flex items-center gap-5">
        <a
          data-testid="footer-email"
          href={MAILTO}
          className="label-mono text-white/70 hover:text-white transition-colors"
        >
          {CONTACT_EMAIL}
        </a>
        <a
          data-testid="footer-capability"
          href={CAPABILITY_PDF}
          download
          className="label-mono text-white/70 hover:text-[#00A859] transition-colors"
        >
          Capability Statement
        </a>
      </div>
    </div>
  </footer>
);

/* ------------------- Main ------------------- */
const Home = () => {
  useReveal();
  return (
    <div data-testid="home-page" className="bg-white">
      <Nav />
      <Hero />
      <WhatWeDo />
      <WhyUs />
      <Results />
      <Capabilities />
      <DownloadBlock />
      <Leadership />
      <Contact />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <Home />
    </div>
  );
}

export default App;
